'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  image: string;
  alt: string;
  primaryCta: string;
  primaryCtaHref: string;
  secondaryCta: string;
  secondaryCtaHref: string;
  overlay: string;
}

const slides: HeroSlide[] = [
{
  id: 1,
  title: 'Global Music Festival 2025',
  subtitle: 'Three days of world-class artists across 5 stages.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11a8f7fce-1775205795850.png',
  alt: 'Massive outdoor music festival with colorful stage lights and large crowd at night',
  primaryCta: 'Get Tickets',
  primaryCtaHref: '#tickets',
  secondaryCta: 'Learn more',
  secondaryCtaHref: '#event-details',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.82) 38%, rgba(26,10,0,0.15) 100%)'
},
{
  id: 2,
  title: 'TechSummit 2025',
  subtitle: "The world's leading technology conference. 200+ speakers.",
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1707cbb16-1773889125784.png',
  alt: 'Large technology conference auditorium with speaker on stage and engaged audience',
  primaryCta: 'Register Now',
  primaryCtaHref: '#register',
  secondaryCta: 'View Agenda',
  secondaryCtaHref: '#agenda',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.85) 38%, rgba(26,10,0,0.18) 100%)'
},
{
  id: 3,
  title: 'City Food & Wine Expo',
  subtitle: 'Taste the world. 500+ vendors. Unlimited discovery.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11592fa6b-1772378523973.png',
  alt: 'Elegant food and wine expo with beautifully plated dishes and wine glasses',
  primaryCta: 'Buy Tickets',
  primaryCtaHref: '#tickets',
  secondaryCta: 'Explore',
  secondaryCtaHref: '#explore',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.80) 38%, rgba(26,10,0,0.12) 100%)'
},
{
  id: 4,
  title: 'Championship Boxing Night',
  subtitle: 'Live. Unfiltered. Unforgettable. The fight of the decade.',
  image: "https://images.unsplash.com/photo-1560191832-d3bb3d4852a9",
  alt: 'Professional boxing ring with bright arena lights and excited crowd',
  primaryCta: 'Get Tickets',
  primaryCtaHref: '#tickets',
  secondaryCta: 'Learn more',
  secondaryCtaHref: '#details',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.85) 38%, rgba(26,10,0,0.15) 100%)'
},
{
  id: 5,
  title: 'Modern Art Biennale',
  subtitle: 'Immersive installations. Boundary-pushing creativity.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a9b91e65-1766505060881.png',
  alt: 'Contemporary art gallery with colorful immersive light installation',
  primaryCta: 'Book Now',
  primaryCtaHref: '#book',
  secondaryCta: 'Explore',
  secondaryCtaHref: '#explore',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.78) 38%, rgba(26,10,0,0.12) 100%)'
},
{
  id: 6,
  title: 'Startup Pitch Night',
  subtitle: 'Meet the next generation of founders. Network. Invest. Inspire.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_136ebe11f-1764672595819.png',
  alt: 'Startup pitch event with entrepreneur presenting to investors in modern office',
  primaryCta: 'Register Free',
  primaryCtaHref: '#register',
  secondaryCta: 'Learn more',
  secondaryCtaHref: '#details',
  overlay: 'linear-gradient(to right, rgba(26,10,0,0.82) 38%, rgba(26,10,0,0.15) 100%)'
}];


export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating || index === current) return;
      setIsAnimating(true);
      setPrevIndex(current);
      setCurrent(index);
      setTimeout(() => {
        setIsAnimating(false);
        setPrevIndex(null);
      }, 700);
    },
    [isAnimating, current]
  );

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <div className="relative w-full overflow-hidden" style={{ height: '100vh', minHeight: '600px', maxHeight: '860px' }}>
      {/* Background Images */}
      {slides.map((s, i) =>
      <div
        key={s.id}
        className="absolute inset-0"
        style={{
          opacity: i === current ? 1 : 0,
          transition: 'opacity 0.7s ease-in-out',
          zIndex: i === current ? 1 : 0
        }}>
        
          <img src={s.image} alt={s.alt} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: s.overlay }} />
          {/* Subtle gold vignette at bottom */}
          <div className="absolute inset-x-0 bottom-0" style={{ height: '200px', background: 'linear-gradient(to top, rgba(26,10,0,0.6), transparent)' }} />
        </div>
      )}

      {/* Ornamental top border */}
      <div className="absolute top-0 left-0 right-0 z-20" style={{ height: '3px', background: 'linear-gradient(to right, transparent, #C9A84C, #E8C97A, #C9A84C, transparent)' }} />

      {/* Content */}
      <div className="relative z-10 h-full flex items-end pb-24 md:pb-28">
        <div className="px-8 md:px-16 lg:px-20 max-w-3xl">
          {/* Ornamental accent line */}
          <div className="flex items-center gap-3 mb-4">
            <div style={{ width: '32px', height: '1px', background: '#C9A84C' }} />
            <span style={{ color: '#C9A84C', fontSize: '11px', letterSpacing: '2.5px', fontFamily: 'Roboto, sans-serif', fontWeight: 500, textTransform: 'uppercase' }}>
              Featured
            </span>
          </div>
          <h1
            key={`title-${current}`}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight animate-fadeInUp"
            style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 }}>
            
            {slide.title}
          </h1>
          <p
            key={`sub-${current}`}
            className="text-lg md:text-xl mb-8 animate-fadeInUp"
            style={{ animationDelay: '0.1s', color: 'rgba(232,201,122,0.85)', fontFamily: 'Roboto, sans-serif' }}>
            
            {slide.subtitle}
          </p>
          <div
            key={`cta-${current}`}
            className="flex flex-wrap gap-4 animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}>
            
            <Link
              href={slide.primaryCtaHref}
              className="flex items-center justify-center h-12 px-8 font-medium text-base transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #C9A84C, #8B6914)',
                color: '#FAF7F2',
                borderRadius: '6px',
                fontSize: '15px',
                fontWeight: 500,
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 16px rgba(201,168,76,0.35)',
              }}>
              
              {slide.primaryCta}
            </Link>
            <Link
              href={slide.secondaryCtaHref}
              className="flex items-center justify-center h-12 px-8 font-medium text-base transition-all duration-200"
              style={{
                background: 'transparent',
                color: '#E8C97A',
                border: '1px solid rgba(201,168,76,0.6)',
                borderRadius: '6px',
                fontSize: '15px',
                fontWeight: 500,
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              
              {slide.secondaryCta}
            </Link>
          </div>
        </div>
      </div>

      {/* Left Arrow */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full transition-colors"
        style={{ background: 'rgba(26,10,0,0.5)', border: '1px solid rgba(201,168,76,0.4)' }}
        aria-label="Previous slide">
        
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3L5 8L10 13" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Right Arrow */}
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full transition-colors"
        style={{ background: 'rgba(26,10,0,0.5)', border: '1px solid rgba(201,168,76,0.4)' }}
        aria-label="Next slide">
        
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 3L11 8L6 13" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dot Indicators — gold style */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 items-center">
        {slides.map((_, i) =>
        <button
          key={i}
          onClick={() => goTo(i)}
          className="transition-all duration-300"
          style={{
            width: i === current ? '28px' : '8px',
            height: '3px',
            borderRadius: '2px',
            background: i === current ? '#C9A84C' : 'rgba(201,168,76,0.35)'
          }}
          aria-label={`Go to slide ${i + 1}`} />

        )}
      </div>
    </div>);

}